'use client';

import { css } from '@repo/pandacss/styled-system/css';
import { type StateAbbreviation } from '@repo/supergraph/marketplace/types';
import type {
  CategorySection as CategorySectionType,
  ProductCard as ProductCardType,
} from '@repo/supergraph/roundup/base/types';
import type { MobileOrDesktop } from '@repo/utils/deviceType/types';
import { useMemo } from 'react';
import CategorySection from './CategorySection';
import {
  createOfferOrderMap,
  filterProductCardsByOffers,
  sortProductCardsByOfferOrder,
  sortSectionsByFirstCardOrder,
} from './utils';

type ProductCardCategorySectionsProps = {
  categorySections: CategorySectionType[];
  deviceType?: MobileOrDesktop;
  isOurTakeExpanded?: boolean;
  pageType?: 'roundup' | 'paid-roundup';
  regionCode?: StateAbbreviation;
  shouldApplyStylingUpdates?: boolean;
  showSkeleton?: boolean;
  offersListToFilterWith?: ProductCardType[];
  isLoading?: boolean;
  isSorted?: boolean;
  slug?: string;
};

export default function ProductCardCategorySections({
  categorySections,
  deviceType,
  isOurTakeExpanded = true,
  pageType,
  regionCode,
  shouldApplyStylingUpdates = false,
  showSkeleton = false,
  offersListToFilterWith,
  isLoading,
  isSorted = false,
  slug,
}: ProductCardCategorySectionsProps) {
  let productPositionBase = -1;

  const filteredSections = useMemo(() => {
    if (!offersListToFilterWith) {
      return categorySections;
    }

    const offerOrderMap = createOfferOrderMap(offersListToFilterWith);

    const sectionsWithProcessedCards = categorySections
      .map(section => {
        const filteredCards = filterProductCardsByOffers(
          section.productCards,
          offersListToFilterWith,
        );

        return {
          ...section,
          productCards: isSorted
            ? sortProductCardsByOfferOrder(filteredCards, offerOrderMap)
            : filteredCards,
        };
      })
      .filter(section => section.productCards.length > 0);

    return isSorted
      ? sortSectionsByFirstCardOrder(sectionsWithProcessedCards, offerOrderMap)
      : sectionsWithProcessedCards;
  }, [categorySections, offersListToFilterWith, isSorted]);

  return (
    <section data-testid="product-cards-container" className={css({ marginTop: '4' })}>
      <ul
        className={css({
          marginBottom: '16',
          paddingTop: '0',
          paddingRight: '0',
          paddingBottom: '0',
          paddingLeft: '0',
        })}
      >
        {filteredSections.map((section, sectionIndex) => {
          productPositionBase += section.productCards?.length || 0;
          return (
            <CategorySection
              key={`${section.title}-${sectionIndex}-${section.productCards?.[0]?.marketplaceEntity.id ?? 'no-product-id'}`}
              deviceType={deviceType}
              title={section.title}
              description={section.description}
              eyebrowText={section.eyebrowText}
              productCards={section.productCards}
              productPositionBase={productPositionBase}
              isOurTakeExpanded={isOurTakeExpanded}
              pageType={pageType}
              regionCode={regionCode}
              shouldApplyStylingUpdates={shouldApplyStylingUpdates}
              showSkeleton={showSkeleton || isLoading}
              slug={slug}
            />
          );
        })}
      </ul>
    </section>
  );
}
